import { motion } from "framer-motion";
import { Star } from "./Star";
import { useI18n } from "@/context/LanguageContext";

export const Hero = () => {
  const { content, t } = useI18n();
  const h = t.hero;
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen bg-[#FFF7F1] overflow-hidden pt-20 md:pt-24"
    >
      <div className="hidden md:block absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#F46555] opacity-90" />
      <div className="hidden md:block absolute top-40 -right-10 w-[180px] h-[180px] rounded-full bg-[#7AD5DC]" />
      <Star size={56} spin className="hidden md:block absolute md:top-44 md:right-24 text-[#141518] z-10" />

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative z-10">
        <div className="flex items-center justify-between font-condensed uppercase tracking-[0.28em] text-[10px] md:text-xs pt-4">
          <motion.span initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="hero-meta-place">
            {h.metaPlace}
          </motion.span>
          <motion.span initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} data-testid="hero-meta-edition">
            {h.metaEdition}
          </motion.span>
        </div>

        <div className="mt-4 border-t-2 border-[#141518]" />

        <div className="relative mt-8 md:mt-12">
          <div className="grid grid-cols-12 gap-4 md:gap-8 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="col-span-12 md:col-span-7">
              <h1 data-testid="hero-title" className="font-display font-bold uppercase leading-[0.86] tracking-tight">
                <span className="block text-[18vw] md:text-[10vw] lg:text-[9vw] text-[#141518]">Ariadna</span>
                <span className="block italic text-[#F0311B] text-[12vw] md:text-[7vw] lg:text-[6vw] -mt-2 md:-mt-4 pl-[10%]">&amp;</span>
                <span className="block text-[18vw] md:text-[10vw] lg:text-[9vw] text-[#141518] -mt-2">Wojtek</span>
              </h1>

              <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-2 md:gap-3">
                <span className="sticker bg-[#7AD5DC]">{h.stickerLove}</span>
                <span className="sticker bg-[#F0311B] text-white border-[#141518]">{h.stickerRock}</span>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.3 }} className="col-span-12 md:col-span-5 relative" data-testid="hero-poster">
              <div className="relative w-full border-[3px] border-[#141518] shadow-hard-lg mx-auto max-w-[360px] md:max-w-none md:rotate-2">
                <img src={content.poster} alt="Ariadna i Wojtek" className="block w-full h-auto" loading="eager" />
              </div>
            </motion.div>
          </div>
        </div>

        <div className="mt-10 md:mt-20 mb-12 md:mb-0 border-t-2 border-[#141518] pt-6 md:pt-8 grid grid-cols-12 gap-5 md:gap-6 items-start md:items-center">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} className="col-span-6 md:col-span-5">
            <div className="font-condensed uppercase text-[10px] tracking-[0.28em] text-[#F0311B]">{h.labelDate}</div>
            <div className="font-display font-bold text-3xl sm:text-4xl md:text-5xl mt-1 text-[#141518]" data-testid="hero-date">{content.dateLabel}</div>
            <div className="text-xs md:text-sm text-[#5A5A5F] mt-1">{h.longDate}</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} className="col-span-6 md:col-span-4">
            <div className="font-condensed uppercase text-[10px] tracking-[0.28em] text-[#F0311B]">{h.labelPlace}</div>
            <div className="font-display font-semibold text-xl sm:text-2xl md:text-3xl mt-1 text-[#141518] leading-tight">{h.venue}</div>
            <div className="text-xs md:text-sm text-[#5A5A5F] mt-1">{h.venueCity}</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6 }} className="col-span-12 md:col-span-3 flex md:justify-end">
            <a href={content.rsvpUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-rsvp-button" className="group inline-flex items-center justify-center gap-3 bg-[#F0311B] text-white w-full sm:w-auto px-6 py-4 text-xs uppercase tracking-[0.22em] md:tracking-[0.25em] font-bold border-2 border-[#141518] shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all">
              {h.cta}
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
